import * as React from 'react';
import { Capacitor } from '@capacitor/core';
import { Button } from '@/components/primitives/Button';
import {
  TRACKING_CONSENT_EVENT,
  getTrackingConsentStatus,
  grantTrackingConsent,
  revokeTrackingConsent,
  type TrackingConsentStatus,
} from '@/utils/trackingManager';
import {
  isCookieBannerSuppressed,
  subscribeCookieBannerSuppression,
} from '@/utils/cookieBannerSuppression';

/**
 * Consent UX for analytics/marketing tracking.
 *
 * Jurisdiction-aware:
 * - EU/EEA/UK/CH/BR (opt-in model): banner renders until the user
 *   explicitly accepts or declines. Nothing fires before that.
 * - US and elsewhere (opt-out model under CCPA/CPRA): banner is
 *   suppressed because `getTrackingConsentStatus()` resolves to
 *   `granted` by default. Opt-out is offered via the persistent
 *   "Your Privacy Choices" footer link (PrivacyChoicesDialog).
 * - GPC signal (any jurisdiction): resolver returns `denied`, banner
 *   is suppressed, and every tracking guard short-circuits.
 * - Native mobile shells: banner never renders (ATT prompt covers iOS).
 */

const CONSENT_KEY = 'cookie_consent_status';
const CONSENT_DATE_KEY = 'cookie_consent_date';

export type ConsentStatus = TrackingConsentStatus;

function persistConsentAudit(status: 'accepted' | 'declined') {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(CONSENT_KEY, status);
    window.localStorage.setItem(CONSENT_DATE_KEY, new Date().toISOString());
  } catch {
    // Ignore storage failures (private mode / browser restrictions).
  }
}

function hasMadeConsentChoice(status: TrackingConsentStatus): boolean {
  return status === 'granted' || status === 'denied';
}

export function CookieConsentBanner() {
  // Lazy initializer: read the resolved consent status on the first
  // render so the banner never flashes for US visitors whose implicit
  // default is `granted`. `App.tsx` only mounts the banner after the
  // initial hydration pass (`hasMounted`), so `window` is always
  // defined here on the client path.
  const [status, setStatus] = React.useState<TrackingConsentStatus>(() => {
    if (typeof window === 'undefined') return 'pending';
    return getTrackingConsentStatus();
  });
  const isNativeApp = React.useMemo(() => Capacitor.isNativePlatform(), []);
  const isSuppressed = React.useSyncExternalStore(
    subscribeCookieBannerSuppression,
    isCookieBannerSuppressed,
    () => false,
  );

  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    const syncConsent = () => {
      setStatus(getTrackingConsentStatus());
    };

    syncConsent();
    window.addEventListener(TRACKING_CONSENT_EVENT, syncConsent);
    window.addEventListener('storage', syncConsent);

    return () => {
      window.removeEventListener(TRACKING_CONSENT_EVENT, syncConsent);
      window.removeEventListener('storage', syncConsent);
    };
  }, []);

  const handleAccept = () => {
    persistConsentAudit('accepted');
    grantTrackingConsent({ adDataSource: 'banner' });
  };

  const handleDecline = () => {
    persistConsentAudit('declined');
    revokeTrackingConsent({ adDataSource: 'banner' });
  };

  const isVisible = !hasMadeConsentChoice(status) && !isNativeApp && !isSuppressed;

  React.useEffect(() => {
    if (typeof document === 'undefined') return;

    if (isVisible) {
      document.body.setAttribute('data-jp-cookie-banner-visible', 'true');
      return () => {
        document.body.removeAttribute('data-jp-cookie-banner-visible');
      };
    }

    document.body.removeAttribute('data-jp-cookie-banner-visible');
  }, [isVisible]);

  // `status` can be:
  // - `granted` for US/opt-out jurisdictions (resolver default) or after
  //   the user clicked Accept -- either way, no banner.
  // - `denied` when the user declined or GPC was detected -- no banner.
  // - `pending` only in opt-in jurisdictions with no explicit choice and
  //   no GPC signal -- this is the only case where the banner renders.
  if (!isVisible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[2147483647] overflow-hidden border-t border-jp-line-edge bg-jp-bg-card font-jp-body shadow-[0_-8px_24px_-12px_rgba(11,16,32,0.18)] pb-safe pl-safe pr-safe"
      role="dialog"
      aria-label="Cookie consent"
    >
      <style>
        {`
          body[data-jp-cookie-banner-visible="true"] charla-widget,
          body[data-jp-cookie-banner-visible="true"] #jp-charla-widget {
            display: none !important;
            visibility: hidden !important;
            pointer-events: none !important;
          }
        `}
      </style>
      <div className="p-4">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="flex-1">
            {/* Before a choice, our own product analytics counts the visit
                (nothing stored in the EU); Decline stops it, Accept also allows
                ad measurement and screen replays. */}
            <p className="text-sm text-jp-fg-primary">
              We use cookies to understand how people use Journal Party and to measure our ads.
            </p>
            <p className="mt-1 text-xs text-jp-fg-secondary">
              Decline turns this off for this browser. You can change it anytime in Settings.{' '}
              <a href="/privacy" className="font-semibold text-jp-accent-cta-text underline underline-offset-2 hover:no-underline">
                Learn more
              </a>
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button variant="tertiary" size="sm" onClick={handleDecline}>
              Decline
            </Button>
            <Button variant="primary" size="sm" onClick={handleAccept}>
              Accept
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CookieConsentBanner;
